import { create } from 'zustand';
import { getDirectorById } from '@/services/data/directors';
import { logger } from '@/utils/logger';

interface DirectorData {
  first_name: string;
  last_name: string;
  avatar_url: string | null; // Now mapped from users table via relation
}

interface DirectorState {
  // Director data by directorId
  directors: Record<string, DirectorData | null>;
  // Loading states by directorId
  loading: Record<string, boolean>;
  // Errors by directorId
  errors: Record<string, Error | null>;
  
  // Actions
  fetchDirector: (directorId: string) => Promise<void>;
  getDirector: (directorId: string) => DirectorData | null;
  getDirectorName: (directorId: string) => string | null;
  clearDirector: (directorId: string) => void;
  clearAllDirectors: () => void;
}

export const useDirectorStore = create<DirectorState>((set, get) => ({
  // Initial state
  directors: {},
  loading: {},
  errors: {},

  // Fetch director from API
  fetchDirector: async (directorId: string) => {
    // Skip if already loading or already loaded
    const state = get();
    if (state.loading[directorId] || state.directors[directorId] !== undefined) {
      return;
    }

    // Set loading state
    set((state) => ({
      loading: { ...state.loading, [directorId]: true },
      errors: { ...state.errors, [directorId]: null },
    }));

    try {
      const result = await getDirectorById(directorId);
      
      if (result.success && result.data) {
        const directorData: DirectorData = {
          first_name: result.data.first_name,
          last_name: result.data.last_name,
          avatar_url: (result.data as any).avatar_url || null, // Mapped from users table
        };
        set((state) => ({
          directors: { ...state.directors, [directorId]: directorData },
          loading: { ...state.loading, [directorId]: false },
          errors: { ...state.errors, [directorId]: null },
        }));
      } else {
        // Store null to indicate we tried but failed
        set((state) => ({
          directors: { ...state.directors, [directorId]: null },
          loading: { ...state.loading, [directorId]: false },
          errors: { ...state.errors, [directorId]: result.error as Error || new Error('Failed to fetch director') },
        }));
        logger.error('Error fetching director:', result.error);
      }
    } catch (error) {
      set((state) => ({
        directors: { ...state.directors, [directorId]: null },
        loading: { ...state.loading, [directorId]: false },
        errors: { ...state.errors, [directorId]: error as Error },
      }));
      logger.error('Error fetching director:', error);
    }
  },

  // Get director data (synchronous)
  getDirector: (directorId: string) => {
    return get().directors[directorId] ?? null;
  },

  // Get formatted director name
  getDirectorName: (directorId: string) => {
    const director = get().directors[directorId];
    if (director) {
      return `${director.first_name} ${director.last_name}`;
    }
    return null;
  },

  // Clear specific director
  clearDirector: (directorId: string) => {
    set((state) => {
      const { [directorId]: _, ...directors } = state.directors;
      const { [directorId]: __, ...loading } = state.loading;
      const { [directorId]: ___, ...errors } = state.errors;
      return { directors, loading, errors };
    });
  },

  // Clear all directors
  clearAllDirectors: () => {
    set({ directors: {}, loading: {}, errors: {} });
  },
}));

